import { motion } from "framer-motion";
import { Baby, GraduationCap, Waves, Heart, Users, Check, Clock, BadgePercent, Medal } from "lucide-react";

const BOOK_URL = "https://portal.pivot1st.co.uk/book/2/class";

const tiers = [
  {
    icon: <Baby size={24} />,
    name: "Baby & Pre-School",
    price: "£18.47",
    unit: "per lesson",
    duration: "30 min",
    accent: "#0EA5E9",
    sibling: true,
    includes: ["Adult-accompanied — you're in the water together", "Urchins, Small Fry, Tiny, Tots, Guppies & Tetra", "Baby Shark — first independent class (same price band)"],
  },
  {
    icon: <GraduationCap size={24} />,
    name: "Children's Classes",
    price: "£16.79",
    unit: "per lesson",
    duration: "30 min",
    accent: "#FF7A1A",
    sibling: true,
    includes: ["Independent children's lessons", "Sharks 1, Sharks 2 & Sharks 3", "Building confident, capable young swimmers"],
  },
  {
    icon: <Medal size={24} />,
    name: "Adult Lessons",
    price: "£16.79",
    unit: "per lesson",
    duration: "30 min",
    accent: "#0A2540",
    includes: ["From complete beginners to improvers", "Triathlon & decathlon training welcome", "Following the STA Adult Learn to Swim programme"],
  },
  {
    icon: <Heart size={24} />,
    name: "Happy Bumps",
    price: "£15.00",
    unit: "per session",
    duration: "45 min",
    accent: "#EC4899",
    includes: ["Gentle aqua natal sessions", "Supportive, low-impact movement", "Relax and connect before baby arrives"],
  },
  {
    icon: <Waves size={24} />,
    name: "Adult Social Swim",
    price: "£7.50",
    unit: "per session",
    duration: "45 min",
    accent: "#14B8A6",
    includes: ["Relaxed, informal swimming", "Practise at your own pace", "All adult abilities welcome"],
  },
  {
    icon: <Users size={24} />,
    name: "Little Tiddler Social",
    price: "£7.50",
    unit: "per session",
    duration: "45 min",
    accent: "#8B5CF6",
    includes: ["Relaxed family social swim", "Splash and play together", "A lovely way to build confidence"],
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="py-24 md:py-32 bg-white" data-testid="pricing-section">
      <div className="container mx-auto px-6 md:px-12">
        <div className="max-w-2xl mb-10">
          <span className="text-sm text-[#0EA5E9] tracking-[0.2em] uppercase font-bold">Pricing</span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-[#0F172A] leading-tight">
            Premium lessons, fairly priced
          </h2>
          <p className="mt-5 text-lg text-[#334155]">
            Every price is set by age group, session length and the real cost of running a warm,
            premium pool — and kept as competitive as we possibly can. No hidden fees, no surprises.
          </p>
        </div>

        <div className="mb-14 grid sm:grid-cols-3 gap-4" data-testid="pricing-values">
          {[
            { icon: <BadgePercent size={20} />, title: "Priced by age & session", desc: "Each class is costed for its age group and lesson length — you only pay for what your child attends." },
            { icon: <Waves size={20} />, title: "A premium pool experience", desc: "A warm 31–33°C pool, small classes and qualified teachers — everything set up to help your child thrive in the water." },
            { icon: <Check size={20} />, title: "Competitive & clear", desc: "We keep prices as low as we can for a premium service, with every cost shown up front." },
          ].map((v) => (
            <div key={v.title} className="rounded-2xl bg-[#F8FAFC] border border-slate-100 p-5" data-testid="pricing-value">
              <span className="grid place-items-center w-10 h-10 rounded-xl bg-[#0EA5E9]/12 text-[#0EA5E9] mb-3">{v.icon}</span>
              <h3 className="font-heading text-base font-bold text-[#0F172A]">{v.title}</h3>
              <p className="mt-1.5 text-sm text-[#64748B] leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {tiers.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className={`relative rounded-[2rem] p-8 md:p-9 flex flex-col transition-all duration-500 ${
                t.featured
                  ? "bg-[#0A2540] text-white shadow-2xl"
                  : "bg-white border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1"
              }`}
              data-testid={`pricing-card-${i}`}
            >
              {t.featured && (
                <span className="absolute top-6 right-6 text-[11px] font-bold uppercase tracking-widest bg-[#FF7A1A] text-white px-3 py-1 rounded-full">
                  Most booked
                </span>
              )}
              <span className="grid place-items-center w-14 h-14 rounded-2xl mb-6 text-white" style={{ backgroundColor: t.accent }}>
                {t.icon}
              </span>
              <h3 className={`font-heading text-xl font-semibold ${t.featured ? "text-white" : "text-[#0F172A]"}`}>{t.name}</h3>
              <div className="mt-4 flex items-baseline gap-2">
                <span className={`font-heading text-4xl font-black ${t.featured ? "text-white" : "text-[#0F172A]"}`}>{t.price}</span>
                <span className={t.featured ? "text-[#94A3B8]" : "text-[#64748B]"}>{t.unit}</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${t.featured ? "bg-white/10 text-white" : "bg-slate-100 text-[#334155]"}`}>
                  <Clock size={13} /> {t.duration}
                </span>
                {t.sibling && (
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#10B981]/12 text-[#0E9F6E]">
                    <BadgePercent size={13} /> Sibling discount
                  </span>
                )}
              </div>

              <ul className="mt-7 space-y-3 flex-1">
                {t.includes.map((inc) => (
                  <li key={inc} className="flex items-start gap-3">
                    <span className="grid place-items-center w-5 h-5 mt-0.5 rounded-full text-white shrink-0" style={{ backgroundColor: t.accent }}>
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span className={t.featured ? "text-[#CBD5E1]" : "text-[#334155]"}>{inc}</span>
                  </li>
                ))}
              </ul>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={`mt-8 text-center font-bold px-6 py-3.5 rounded-full transition-all duration-300 ${
                  t.featured ? "bg-[#FF7A1A] text-white hover:-translate-y-0.5" : "bg-[#0A2540] text-white hover:bg-[#0EA5E9]"
                }`}
                data-testid={`pricing-book-${i}`}
              >
                Book this class
              </a>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 space-y-2">
          <p className="flex items-center gap-2 text-sm font-semibold text-[#0E9F6E]">
            <BadgePercent size={15} /> Sibling discounts available on Baby &amp; Pre-School and Children's classes — get in touch for details. (Adult lessons are individually priced.)
          </p>
          <p className="text-sm text-[#64748B]">
            Happy Bumps, Little Tiddler Social and Adult Social Swim are 45-minute sessions; all other classes are 30 minutes. Prices may be subject to change and places are secured through our booking system.
          </p>
        </div>
      </div>
    </section>
  );
}
